import {
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconFaceId,
  IconInfoCircle,
  IconLineScan,
  IconLockAccess,
  IconMapPin,
  IconMessage2Cancel,
  IconMessageCancel,
  IconScan,
} from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { PolicyConfig } from '../../types/SettingsType.ts'
import { useDispatch, useSelector } from 'react-redux'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { selectTravel, setTravelPolicy } from '../../reducers/travel/travel.slice.ts'
import { fetchTravelPolicyWithId } from '../../reducers/travel/travel.thunk.ts'
import { useAppDispatch } from '../../reducers/store.ts'
import { selectDevices } from '../../reducers/devices/devices.slice.ts'
import { fetchSupportedIosVersions } from '../../reducers/devices/devices.thunk.ts'
import { Tooltip } from '@radix-ui/themes'

interface ICreatePolicyRulesProps {
  setIsNextEnabled: (enabled: boolean) => void
  onPoliciesSelected: (policies: Partial<PolicyConfig>) => void
  policyRules: Partial<PolicyConfig>
  policyId?: number
}

function CreatePolicyRules(props: ICreatePolicyRulesProps) {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const { setIsNextEnabled, onPoliciesSelected, policyRules, policyId } = props
  const { session } = useSelector(selectUser)
  const { iosSupportedVersions } = useSelector(selectDevices)
  const { travelPolicy } = useSelector(selectTravel)

  const [sensitiveTravelEnabled, setSensitiveTravelEnabled] = useState<boolean>(false)
  const [selectedPolicies, setSelectedPolicies] = useState({
    deviceIntegrityPeriod: false,
    smishingEnabled: false,
    biometricsEnabled: false,
    screenLockEnabled: false,
    alertSeverity: false,
    travelEnabled: false,
    iosMinimumVersion: false,
    iosVersionDrift: false,
    androidMinimumVersion: false,
    androidLastPatchVersion: false,
    iosTravelEnabled: false,
  })

  const [selectedIOSOption, setSelectedIOSOption] = useState<string | undefined>(undefined)

  const [policyConfig, setPolicyConfig] = useState({
    deviceIntegrityPeriod: 'THREE_DAYS',
    alertSeverity: 'HIGH_AND_CRITICAL',
    travelRiskPeriod: 'ONE_DAY',
    travelRiskLevel: 'HIGH',
    smishingEnabled: false,
    biometricsEnabled: false,
    screenLockEnabled: false,

    iosMinimumVersion: '26.1',
    iosVersionOffset: '0',
    iosMinimumVersionGracePeriod: undefined,
    iosMinimumVersionGracePeriodUnit: 'DAYS',

    androidMinimumVersion: '16',
    androidLastPatchVersionGracePeriod: undefined,
    androidLastPatchVersionGracePeriodUnit: 'DAYS',
  })

  const updatePolicyConfig = (key: string, value: string) => {
    setPolicyConfig((prev) => ({ ...prev, [key]: value }))
  }

  useEffect(() => {
    const hasSelection = Object.values(selectedPolicies).some(Boolean)
    setIsNextEnabled(hasSelection)

    const activeConfigs = Object.entries(selectedPolicies)
      .filter(([_, enabled]) => enabled)
      .reduce(
        (acc, [key]) => {
          if (key === 'travelEnabled') {
            return {
              ...acc,
              travelEnabled: selectedPolicies.travelEnabled,
              travelRiskPeriod: policyConfig.travelRiskPeriod ?? 'ONE_DAY',
              travelRiskLevel: policyConfig.travelRiskLevel ?? 'HIGH',
            }
          }

          if (key === 'iosMinimumVersion') {
            return {
              ...acc,
              iosMinimumVersion: policyConfig.iosMinimumVersion,
            }
          }

          if (key === 'iosVersionDrift') {
            return {
              ...acc,
              iosVersionOffset: policyConfig.iosVersionOffset,
              iosMinimumVersionGracePeriod: policyConfig.iosMinimumVersionGracePeriod,
              iosMinimumVersionGracePeriodUnit: policyConfig.iosMinimumVersionGracePeriodUnit,
            }
          }

          if (key === 'androidMinimumVersion') {
            return {
              ...acc,
              androidMinimumVersion: policyConfig.androidMinimumVersion,
            }
          }

          if (key === 'androidLastPatchVersion') {
            return {
              ...acc,
              androidLastPatchVersionGracePeriod: policyConfig.androidLastPatchVersionGracePeriod,
              androidLastPatchVersionGracePeriodUnit: policyConfig.androidLastPatchVersionGracePeriodUnit,
            }
          }

          const typedKey = key as keyof typeof policyConfig
          return {
            ...acc,
            [key]:
              typeof policyConfig[typedKey] === 'boolean'
                ? selectedPolicies[key as keyof typeof selectedPolicies]
                : policyConfig[typedKey],
          }
        },
        {} as Partial<typeof policyConfig>
      )

    onPoliciesSelected(activeConfigs)
  }, [selectedPolicies, policyConfig])

  useEffect(() => {
    if (!policyRules) return

    di(fetchSupportedIosVersions())

    setPolicyConfig((prev) => ({
      ...prev,
      ...Object.fromEntries(Object.entries(policyRules).filter(([_, v]) => v !== undefined)),
    }))
    const updatedSelections = { ...selectedPolicies }

    if (policyRules.iosMinimumVersion) {
      updatedSelections.iosMinimumVersion = true
      setSelectedIOSOption('minimum')
    }
    if (policyRules.iosVersionOffset) {
      updatedSelections.iosVersionDrift = true
      setSelectedIOSOption('drift')
    }
    if (policyRules.androidMinimumVersion) {
      updatedSelections.androidMinimumVersion = true
    }
    if (policyRules.androidLastPatchVersionGracePeriod) {
      updatedSelections.androidLastPatchVersion = true
    }
    if (policyRules.deviceIntegrityPeriod && policyRules.deviceIntegrityPeriod !== 'NEVER') {
      updatedSelections.deviceIntegrityPeriod = true
    }
    if (policyRules.smishingEnabled) {
      updatedSelections.smishingEnabled = true
    }
    if (policyRules.biometricsEnabled) {
      updatedSelections.biometricsEnabled = true
    }
    if (policyRules.screenLockEnabled) {
      updatedSelections.screenLockEnabled = true
    }
    if (policyRules.alertSeverity && policyRules.alertSeverity !== 'NONE') {
      updatedSelections.alertSeverity = true
    }
    if (policyRules.travelRiskLevel !== undefined && policyRules.travelRiskLevel !== 'NONE') {
      updatedSelections.travelEnabled = true
    }

    setSelectedPolicies(updatedSelections)
  }, [])

  useEffect(() => {
    if (session?.portalPreferences?.sensitiveTravel == true && policyRules) {
      setSensitiveTravelEnabled(true)
    }
  }, [session, policyRules])

  useEffect(() => {
    if (sensitiveTravelEnabled && policyId) {
      di(fetchTravelPolicyWithId(policyId))
    } else {
      dispatch(setTravelPolicy(undefined))
    }
  }, [sensitiveTravelEnabled, policyId])

  useEffect(() => {
    if (travelPolicy) {
      setSelectedPolicies((prev) => ({
        ...prev,
        travelEnabled: true,
      }))
      setPolicyConfig((prev) => ({
        ...prev,
        travelRiskLevel: travelPolicy.riskLevel,
        travelRiskPeriod: travelPolicy.riskPeriod,
      }))
    } else {
      setSelectedPolicies((prev) => ({
        ...prev,
        travelEnabled: false,
      }))
    }
  }, [travelPolicy])

  return (
    <div className="w-full max-w-[1200px] pb-4 px-4 mx-auto flex justify-center items-start gap-2 overflow-hidden mb-14">
      <div className="w-[350px] min-h-[672px] self-stretch px-4 py-2 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-mainBorder inline-flex flex-col justify-start items-start gap-6">
        <div className="w-full flex flex-col justify-start items-start">
          <div className="flex flex-col justify-center items-start">
            <div className="justify-center text-text-Neutral-Primary text-base font-semibold mt-3">Add Rules</div>
          </div>
          <div className="w-full justify-center text-black text-sm">Select rules below to add them to the policy</div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div className="text-center justify-center text-textTertiary text-sm">iOS Only</div>
            <div className="flex-1 h-px bg-mainBorder"></div>
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconBrandApple size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Fixed minimum version</div>
            <input
              type="checkbox"
              checked={selectedIOSOption === 'minimum'}
              onChange={(e) => {
                e.target.checked ? setSelectedIOSOption('minimum') : setSelectedIOSOption(undefined)
                setSelectedPolicies((prev) => ({
                  ...prev,
                  iosMinimumVersion: e.target.checked,
                  iosVersionDrift: false,
                }))
              }}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer aspect-square"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconBrandApple size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Dynamic minimum version</div>
            <input
              type="checkbox"
              checked={selectedIOSOption === 'drift'}
              onChange={(e) => {
                e.target.checked ? setSelectedIOSOption('drift') : setSelectedIOSOption(undefined)
                setSelectedPolicies((prev) => ({
                  ...prev,
                  iosMinimumVersion: false,
                  iosVersionDrift: e.target.checked,
                }))
                updatePolicyConfig('iosMinimumVersionGracePeriod', '0')
                updatePolicyConfig('iosMinimumVersionGracePeriodUnit', 'DAYS')
              }}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer aspect-square"
            />
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div className="text-center justify-center text-textTertiary text-sm">Android Only</div>
            <div className="flex-1 h-px bg-mainBorder"></div>
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconBrandAndroid size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Minimum major version</div>
            <input
              type="checkbox"
              checked={selectedPolicies.androidMinimumVersion}
              onChange={(e) => {
                setSelectedPolicies((prev) => ({ ...prev, androidMinimumVersion: e.target.checked }))
              }}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer aspect-square"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconBrandAndroid size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Minimum security patch</div>
            <input
              type="checkbox"
              checked={selectedPolicies.androidLastPatchVersion}
              onChange={(e) => {
                setSelectedPolicies((prev) => ({ ...prev, androidLastPatchVersion: e.target.checked }))

                updatePolicyConfig('androidLastPatchVersionGracePeriod', '0')
                updatePolicyConfig('androidLastPatchVersionGracePeriodUnit', 'DAYS')
              }}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer aspect-square"
            />
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div className="text-center justify-center text-textTertiary text-sm">All Devices</div>
            <div className="flex-1 h-px bg-mainBorder"></div>
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconAlertTriangle size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Alert severity level</div>
            <input
              type="checkbox"
              checked={selectedPolicies.alertSeverity}
              onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, alertSeverity: e.target.checked }))}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer aspect-square"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconFaceId size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">
              Biometric authentication required
            </div>
            <input
              type="checkbox"
              checked={selectedPolicies.biometricsEnabled}
              onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, biometricsEnabled: e.target.checked }))}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconScan size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Check-in scan recency</div>
            <input
              type="checkbox"
              checked={selectedPolicies.deviceIntegrityPeriod}
              onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, deviceIntegrityPeriod: e.target.checked }))}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconLockAccess size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Screen lock required</div>
            <input
              type="checkbox"
              checked={selectedPolicies.screenLockEnabled}
              onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, screenLockEnabled: e.target.checked }))}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer"
            />
          </div>
          <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
            <div className="w-4 h-4 relative overflow-hidden">
              <IconMessage2Cancel size={16} />
            </div>
            <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Smishing protection required</div>
            <input
              type="checkbox"
              checked={selectedPolicies.smishingEnabled}
              onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, smishingEnabled: e.target.checked }))}
              className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer"
            />
          </div>
          {sensitiveTravelEnabled && (
            <>
              <div className="self-stretch px-2 py-1.5 rounded inline-flex justify-start items-center gap-2">
                <div className="w-4 h-4 relative overflow-hidden">
                  <IconMapPin size={16} />
                </div>
                <div className="flex-1 justify-center text-text-Neutral-Primary text-sm">Location Sharing</div>
                <input
                  type="checkbox"
                  checked={selectedPolicies.travelEnabled}
                  onChange={(e) => setSelectedPolicies((prev) => ({ ...prev, travelEnabled: e.target.checked }))}
                  className="h-3 w-3 appearance-none rounded-sm border border-mainBorder checked:bg-mainPurple checked:border-mainPurple focus:outline-none focus:ring-0 cursor-pointer"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 self-stretch px-4 py-2 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-mainBorder inline-flex flex-col justify-start items-start gap-6">
        <div className="flex flex-col justify-start items-start">
          <div className="flex flex-col justify-center items-start">
            <div className="justify-center text-text-Neutral-Primary text-base font-semibold mt-3">Policy Rules</div>
          </div>
          <div className="justify-center text-black text-sm">
            The following rules define compliance requirements across all devices
          </div>
        </div>
        <div className="self-stretch flex-1 flex flex-col justify-start items-center gap-2">
          <div className="flex flex-col gap-4 py-2 w-full mx-auto">
            {!Object.values(selectedPolicies).some(Boolean) && (
              <div className="w-full flex mx-auto justify-center text-textTertiary text-sm">No Rules added</div>
            )}

            {selectedPolicies.iosMinimumVersion && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconBrandApple size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires <span className="font-semibold">iOS</span> devices to run version
                  </p>
                  <select
                    className="h-[36px] w-[84px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.iosMinimumVersion}
                    onChange={(event) => {
                      updatePolicyConfig('iosMinimumVersion', event.target.value)
                    }}
                  >
                    {iosSupportedVersions &&
                      iosSupportedVersions.map((version: string) => (
                        <option key={version} value={version}>
                          {version}
                        </option>
                      ))}
                  </select>
                  <p className="mt-2 text-sm"> or newer</p>
                </div>
              </div>
            )}

            {selectedPolicies.iosVersionDrift && (
              <div className="w-full h-[116px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder">
                <div className="flex gap-2">
                  <IconBrandApple size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires <span className="font-semibold">iOS</span> devices to be within
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className={`h-[36px] w-[70px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled`}
                    value={policyConfig.iosVersionOffset}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '') {
                        updatePolicyConfig('iosVersionOffset', '')
                        return
                      }
                      if (/^\d+$/.test(value)) {
                        updatePolicyConfig('iosVersionOffset', value)
                      }
                    }}
                    onBlur={(event) => {
                      const value = event.target.value.trim()
                      if (value === '' || Number.isNaN(Number(value)) || Number(value) < 0) {
                        updatePolicyConfig('iosVersionOffset', '0')
                      } else {
                        updatePolicyConfig('iosVersionOffset', String(Math.floor(Number(value))))
                      }
                    }}
                  />
                  <p className="mt-2 text-sm"> versions of the latest release</p>
                  <div className="flex-1"></div>
                  <div className="flex justify-center items-center">
                    <Tooltip
                      content="Version counts include full iOS versions, not just major releases. For example, iOS 26.1 and iOS 26.2 are considered separate versions when determining how many versions behind the latest release a device is allowed to be."
                      side="top"
                      align="end"
                      className="max-w-[340px]"
                    >
                      <IconInfoCircle size={20} className="text-textTertiary" />
                    </Tooltip>
                  </div>
                </div>
                <div className="w-full h-[52px] px-4 py-2 bg-white rounded-lg outline outline-1 outline-mainBorder flex mt-2 gap-2">
                  <p className="mt-2 text-sm">Allow devices</p>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className={`h-[36px] w-[50px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled`}
                    value={policyConfig.iosMinimumVersionGracePeriod ?? ''}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '') {
                        updatePolicyConfig('iosMinimumVersionGracePeriod', '')
                        return
                      }
                      if (/^\d+$/.test(value)) {
                        updatePolicyConfig('iosMinimumVersionGracePeriod', value)
                      }
                    }}
                    onBlur={(event) => {
                      const value = event.target.value.trim()
                      if (value === '' || Number.isNaN(Number(value)) || Number(value) < 0) {
                        updatePolicyConfig('iosMinimumVersionGracePeriod', '0')
                      } else {
                        updatePolicyConfig('iosMinimumVersionGracePeriod', String(Math.floor(Number(value))))
                      }
                    }}
                  />
                  <select
                    className="h-[36px] w-[84px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.iosMinimumVersionGracePeriodUnit}
                    onChange={(event) => {
                      updatePolicyConfig('iosMinimumVersionGracePeriodUnit', event.target.value)
                    }}
                  >
                    <option value="DAYS">Days</option>
                    <option value="WEEKS">Weeks</option>
                  </select>
                  <p className="mt-2 text-sm">before failing policy after a new release</p>
                </div>
              </div>
            )}

            {selectedPolicies.androidMinimumVersion && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder">
                <div className="flex gap-2">
                  <IconBrandAndroid size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires <span className="font-semibold">Android</span> devices to run version
                  </p>
                  <select
                    className="h-[36px] w-[84px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.androidMinimumVersion}
                    onChange={(event) => {
                      updatePolicyConfig('androidMinimumVersion', event.target.value)
                    }}
                  >
                    <option value="16">16</option>
                    <option value="15">15</option>
                    <option value="14">14</option>
                    <option value="13">13</option>
                    <option value="12">12</option>
                    <option value="11">11</option>
                    <option value="10">10</option>
                    <option value="9">9</option>
                  </select>
                  <p className="mt-2 text-sm"> or newer</p>
                </div>
              </div>
            )}

            {selectedPolicies.androidLastPatchVersion && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder">
                <div className="flex gap-2">
                  <IconBrandAndroid size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires <span className="font-semibold">Android</span> devices to update their patch level within
                  </p>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className={`h-[36px] w-[50px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled`}
                    value={policyConfig.androidLastPatchVersionGracePeriod ?? ''}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '') {
                        updatePolicyConfig('androidLastPatchVersionGracePeriod', '')
                        return
                      }
                      if (/^\d+$/.test(value)) {
                        updatePolicyConfig('androidLastPatchVersionGracePeriod', value)
                      }
                    }}
                    onBlur={(event) => {
                      const value = event.target.value.trim()
                      if (value === '' || Number.isNaN(Number(value)) || Number(value) < 0) {
                        updatePolicyConfig('androidLastPatchVersionGracePeriod', '0')
                      } else {
                        updatePolicyConfig('androidLastPatchVersionGracePeriod', String(Math.floor(Number(value))))
                      }
                    }}
                  />
                  <select
                    className="h-[36px] w-[84px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.androidLastPatchVersionGracePeriodUnit}
                    onChange={(event) => {
                      updatePolicyConfig('androidLastPatchVersionGracePeriodUnit', event.target.value)
                    }}
                  >
                    <option value="DAYS">Days</option>
                    <option value="WEEKS">Weeks</option>
                  </select>
                  <div className="flex-1"></div>
                  <div className="flex justify-center items-center">
                    <Tooltip
                      content="Android security patches are released on a monthly cadence. As a guideline, 30 days typically equals one security patch cycle. Devices must apply a patch within the number of days you specify to remain compliant."
                      side="top"
                      align="end"
                      className="max-w-[340px]"
                    >
                      <IconInfoCircle size={20} className="text-textTertiary" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}

            {selectedPolicies.alertSeverity && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconAlertTriangle size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Require devices to have no <span className="font-semibold">Open Alerts</span> of{' '}
                    <span className="font-semibold">Severity</span>
                  </p>
                  <select
                    className={`h-[36px] w-[190px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled`}
                    value={policyConfig.alertSeverity}
                    onChange={(event) => {
                      updatePolicyConfig('alertSeverity', event.target.value)
                    }}
                  >
                    <option value="CRITICAL_ONLY">Critical Only</option>
                    <option value="HIGH_AND_CRITICAL">High and Critical</option>
                    <option value="NONE">None</option>
                  </select>
                </div>
              </div>
            )}

            {selectedPolicies.biometricsEnabled && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconFaceId size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires devices to <span className="font-semibold">Enable Biometrics</span>
                  </p>
                </div>
              </div>
            )}

            {selectedPolicies.deviceIntegrityPeriod && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconLineScan size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires devices to <span className="font-semibold">Check-In</span> every
                  </p>
                  <select
                    className={`h-[36px] w-[190px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled`}
                    value={policyConfig.deviceIntegrityPeriod}
                    onChange={(event) => {
                      updatePolicyConfig('deviceIntegrityPeriod', event.target.value)
                    }}
                  >
                    <option value="THREE_DAYS">3 Days</option>
                    <option value="SEVEN_DAYS">7 Days</option>
                    <option value="TWO_WEEKS">2 Weeks</option>
                    <option value="NEVER">Never</option>
                  </select>
                </div>
              </div>
            )}

            {selectedPolicies.screenLockEnabled && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconLockAccess size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires devices to <span className="font-semibold">Enable Screen Lock</span>
                  </p>
                </div>
              </div>
            )}

            {selectedPolicies.smishingEnabled && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconMessageCancel size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires devices to <span className="font-semibold">Enable Smishing Protection</span>
                  </p>
                </div>
              </div>
            )}

            {selectedPolicies.travelEnabled && sensitiveTravelEnabled && (
              <div className="w-full h-[52px] px-4 py-2 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder flex justify-between">
                <div className="flex gap-2">
                  <IconMapPin size={20} className="mt-2" />
                  <p className="mt-2 text-sm">
                    Requires <span className="font-semibold">iOS</span> devices to share location (
                    <span className="font-semibold">Android is enabled automatically</span>)
                  </p>
                  {/* <select
                    className="h-[36px] w-[90px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.travelRiskPeriod}
                    onChange={(event) => {
                      updatePolicyConfig('travelRiskPeriod', event.target.value)
                    }}
                  >
                    <option value="ONE_DAY">1 Day</option>
                    <option value="THREE_DAYS">3 Days</option>
                    <option value="SEVEN_DAYS">7 Days</option>
                  </select>
                  <p className="mt-2 text-sm">for</p>
                  <select
                    className="h-[36px] w-[190px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                    value={policyConfig.travelRiskLevel}
                    onChange={(event) => {
                      updatePolicyConfig('travelRiskLevel', event.target.value)
                    }}
                  >
                    <option value="HIGH">High Risk Only</option>
                    <option value="HIGH_AND_MEDIUM">Medium and High Risk</option>
                    <option value="NONE">None</option>
                  </select>
                  <p className="mt-2 text-sm">countries</p> */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreatePolicyRules
